export interface HelpArticle {
  id: string;
  title: string;
  description: string;
  content: string;
  tags?: string[];
  videoUrl?: string;
  popular?: boolean;
  relatedArticles?: string[];
}

export interface HelpCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  articles: HelpArticle[];
}

export interface HelpContent {
  categories: HelpCategory[];
}

export const helpContent: HelpContent = {
  categories: [
    {
      id: 'getting-started',
      name: 'Getting Started',
      description: 'Essential guides to get you up and running',
      icon: 'article',
      articles: [
        {
          id: 'first-login',
          title: 'First Login Guide',
          description: 'Learn how to navigate your portal after first login',
          popular: true,
          tags: ['beginner', 'setup'],
          content: `
            <h2>Welcome to Your Portal!</h2>
            <p>Congratulations on logging in for the first time. Here's what you need to know:</p>

            <h3>1. Dashboard Overview</h3>
            <p>Your dashboard shows:</p>
            <ul>
              <li><strong>Active Calls:</strong> See your current calls in real-time</li>
              <li><strong>Recent Activity:</strong> Quick access to call history and voicemails</li>
              <li><strong>Quick Actions:</strong> Make calls, check voicemail, view settings</li>
            </ul>

            <h3>2. Navigation</h3>
            <p>The sidebar menu gives you access to:</p>
            <ul>
              <li><strong>Dashboard:</strong> Your home screen</li>
              <li><strong>Call History:</strong> View all your past calls</li>
              <li><strong>Voicemail:</strong> Listen to messages</li>
              <li><strong>Extensions:</strong> Manage your phone extensions</li>
              <li><strong>Settings:</strong> Configure your preferences</li>
            </ul>

            <h3>3. Making Your First Call</h3>
            <p>To make a call:</p>
            <ol>
              <li>Click the phone icon in the top navigation</li>
              <li>Enter the phone number (with area code)</li>
              <li>Select which extension to call from</li>
              <li>Click "Call" to connect</li>
            </ol>

            <h3>4. Next Steps</h3>
            <p>We recommend:</p>
            <ul>
              <li>Setting up your voicemail greeting</li>
              <li>Configuring your E911 emergency address</li>
              <li>Adding your team members as extensions</li>
              <li>Exploring the call forwarding options</li>
            </ul>

            <p><strong>Need help?</strong> Click the help icon (?) in the top right corner anytime!</p>
          `,
          relatedArticles: ['dashboard-overview', 'making-calls', 'voicemail-setup']
        },
        {
          id: 'dashboard-overview',
          title: 'Understanding Your Dashboard',
          description: 'Learn about all the features on your main dashboard',
          popular: true,
          tags: ['dashboard', 'overview'],
          content: `
            <h2>Dashboard Overview</h2>
            <p>Your dashboard is your command center for all phone activities.</p>

            <h3>Top Navigation Bar</h3>
            <ul>
              <li><strong>Company Logo:</strong> Click to return home</li>
              <li><strong>Search:</strong> Quickly find call history or contacts</li>
              <li><strong>Call Button:</strong> Make outbound calls</li>
              <li><strong>Notifications:</strong> Missed calls and voicemails</li>
              <li><strong>Help:</strong> Access this help center</li>
              <li><strong>Profile:</strong> Your account settings</li>
            </ul>

            <h3>Active Calls Widget</h3>
            <p>When you or your team are on calls, you'll see:</p>
            <ul>
              <li>Who is on the call</li>
              <li>Call duration</li>
              <li>Direction (inbound/outbound)</li>
              <li>Caller information</li>
            </ul>

            <h3>Statistics Cards</h3>
            <p>Quick stats show:</p>
            <ul>
              <li><strong>Total Calls Today:</strong> Inbound and outbound counts</li>
              <li><strong>Answer Rate:</strong> Percentage of answered vs missed</li>
              <li><strong>Total Duration:</strong> Time spent on calls</li>
              <li><strong>New Voicemails:</strong> Unheard messages</li>
            </ul>

            <h3>Recent Call History</h3>
            <p>View your last 10 calls with options to:</p>
            <ul>
              <li>Play recordings (if enabled)</li>
              <li>Return calls</li>
              <li>View call details</li>
            </ul>
          `,
          relatedArticles: ['first-login', 'call-history']
        },
        {
          id: 'making-calls',
          title: 'Making Your First Call',
          description: 'Step-by-step guide to making outbound calls',
          popular: true,
          tags: ['calls', 'outbound', 'beginner'],
          content: `
            <h2>How to Make a Call</h2>
            <p>Making calls from your portal is easy. Here are several ways to do it:</p>

            <h3>Method 1: Using the Dial Pad</h3>
            <ol>
              <li>Click the <strong>Phone icon</strong> in the top navigation</li>
              <li>Enter the phone number using the dial pad</li>
              <li>Make sure to include the area code (e.g., 289-555-1234)</li>
              <li>Select which extension you want to call from (if you have multiple)</li>
              <li>Click <strong>"Call"</strong> or press Enter</li>
            </ol>

            <h3>Method 2: From Call History</h3>
            <ol>
              <li>Go to <strong>Call History</strong> in the sidebar</li>
              <li>Find the contact or number you want to call</li>
              <li>Click the phone icon next to their name</li>
              <li>The call will initiate immediately</li>
            </ol>

            <h3>Method 3: Click-to-Call (if configured)</h3>
            <p>If your browser extension is installed, you can click phone numbers on any webpage to dial them.</p>

            <h3>Important Notes</h3>
            <ul>
              <li><strong>Format:</strong> You can enter numbers as 2895551234, 289-555-1234, or (289) 555-1234</li>
              <li><strong>International:</strong> For international calls, include + and country code (e.g., +44 20 1234 5678)</li>
              <li><strong>Extensions:</strong> For internal calls, just dial the 3-4 digit extension number</li>
            </ul>

            <h3>During a Call</h3>
            <p>You can:</p>
            <ul>
              <li>Mute your microphone</li>
              <li>Put the call on hold</li>
              <li>Transfer to another extension</li>
              <li>Start call recording (if enabled)</li>
            </ul>

            <h3>Troubleshooting</h3>
            <p>If calls aren't working:</p>
            <ul>
              <li>Check that your phone is registered (green status light)</li>
              <li>Verify you have sufficient balance/credits</li>
              <li>Ensure your browser has microphone permissions</li>
              <li>Contact support if issues persist</li>
            </ul>
          `,
          relatedArticles: ['call-history', 'extensions-setup']
        },
        {
          id: 'voicemail-setup',
          title: 'Setting Up Your Voicemail',
          description: 'Configure voicemail greetings and notifications',
          popular: true,
          tags: ['voicemail', 'setup'],
          content: `
            <h2>Voicemail Setup Guide</h2>
            <p>Set up your professional voicemail in just a few minutes.</p>

            <h3>Step 1: Access Voicemail Settings</h3>
            <ol>
              <li>Click <strong>Voicemail</strong> in the sidebar</li>
              <li>Click the <strong>Settings</strong> icon (gear)</li>
              <li>You'll see your voicemail configuration options</li>
            </ol>

            <h3>Step 2: Record Your Greeting</h3>
            <p>You have three options:</p>
            <ol>
              <li><strong>Record via phone:</strong> Call your voicemail box and follow prompts</li>
              <li><strong>Upload audio file:</strong> Upload a pre-recorded MP3/WAV file</li>
              <li><strong>Text-to-Speech:</strong> Type your greeting and let the system speak it</li>
            </ol>

            <h3>Professional Greeting Template</h3>
            <p><em>"Hello, you've reached [Your Name] at [Company Name]. I'm unable to take your call right now. Please leave your name, number, and a brief message, and I'll get back to you as soon as possible. Thank you!"</em></p>

            <h3>Step 3: Configure Notifications</h3>
            <ul>
              <li><strong>Email to inbox:</strong> Get voicemail MP3s sent to your email</li>
              <li><strong>SMS alerts:</strong> Text notification when you have new messages</li>
              <li><strong>Push notifications:</strong> Real-time alerts in your portal</li>
            </ul>

            <h3>Step 4: Set Voicemail Options</h3>
            <ul>
              <li><strong>Rings before voicemail:</strong> How many rings (default: 4)</li>
              <li><strong>Max message length:</strong> Typical is 2-3 minutes</li>
              <li><strong>Require PIN:</strong> For added security when checking messages</li>
              <li><strong>Delete after email:</strong> Auto-delete from server after emailing</li>
            </ul>

            <h3>Checking Your Voicemail</h3>
            <p>Three ways to check messages:</p>
            <ol>
              <li><strong>Portal:</strong> Click Voicemail in sidebar, click play on any message</li>
              <li><strong>Email:</strong> Listen to attached MP3 file</li>
              <li><strong>Phone:</strong> Dial *97 or your voicemail access number</li>
            </ol>

            <h3>Tips for Better Voicemail</h3>
            <ul>
              <li>Keep greeting under 20 seconds</li>
              <li>Speak clearly and at a moderate pace</li>
              <li>Update greeting if you're on vacation</li>
              <li>Check messages at least daily</li>
              <li>Archive important messages instead of deleting</li>
            </ul>
          `,
          relatedArticles: ['notifications-setup', 'call-forwarding']
        }
      ]
    },
    {
      id: 'call-management',
      name: 'Call Management',
      description: 'Learn how to manage your calls effectively',
      icon: 'article',
      articles: [
        {
          id: 'call-history',
          title: 'Using Call History',
          description: 'View, search, and manage your call records',
          popular: true,
          tags: ['calls', 'history', 'recordings'],
          content: `
            <h2>Call History Guide</h2>
            <p>Your call history shows all inbound, outbound, and internal calls.</p>

            <h3>Accessing Call History</h3>
            <ol>
              <li>Click <strong>Call History</strong> in the sidebar</li>
              <li>You'll see your recent calls by default</li>
              <li>Use filters to narrow down results</li>
            </ol>

            <h3>Understanding Call Records</h3>
            <p>Each call shows:</p>
            <ul>
              <li><strong>Date & Time:</strong> When the call occurred</li>
              <li><strong>Direction:</strong> Inbound (incoming), Outbound (outgoing), or Internal</li>
              <li><strong>Remote Number:</strong> The other party's phone number</li>
              <li><strong>Caller ID Name:</strong> If available from caller ID database</li>
              <li><strong>Duration:</strong> Length of the call in minutes:seconds</li>
              <li><strong>Status:</strong> Answered, Missed, Busy, etc.</li>
              <li><strong>Recording:</strong> Play button if recording is available</li>
            </ul>

            <h3>Filtering Calls</h3>
            <p>Use the filters at the top to find specific calls:</p>
            <ul>
              <li><strong>Date Range:</strong> Choose start and end dates</li>
              <li><strong>Direction:</strong> Filter by inbound/outbound/internal</li>
              <li><strong>Search Number:</strong> Find calls to/from a specific number</li>
              <li><strong>Extension:</strong> Filter by which extension was used</li>
            </ul>

            <h3>Working with Recordings</h3>
            <p>If call recording is enabled:</p>
            <ul>
              <li><strong>Play:</strong> Click play button to listen in browser</li>
              <li><strong>Download:</strong> Save MP3 to your computer</li>
              <li><strong>Share:</strong> Copy link to share with others</li>
            </ul>

            <p><strong>Note:</strong> Recordings typically appear 30-60 seconds after call ends.</p>

            <h3>Call Statistics</h3>
            <p>At the top, you'll see summary statistics:</p>
            <ul>
              <li><strong>Total Calls:</strong> Count by direction</li>
              <li><strong>Answer Rate:</strong> Percentage of answered calls</li>
              <li><strong>Total Duration:</strong> Time spent on calls</li>
              <li><strong>Average Duration:</strong> Average call length</li>
              <li><strong>Unique Numbers:</strong> How many different contacts</li>
            </ul>

            <h3>Exporting Call Data</h3>
            <p>Need to export for reporting? (Coming Soon)</p>
            <ul>
              <li>Click "Export" button</li>
              <li>Choose date range and format (CSV/Excel)</li>
              <li>Download includes all visible filtered records</li>
            </ul>

            <h3>Mobile View</h3>
            <p>On mobile devices, call history is optimized for smaller screens:</p>
            <ul>
              <li>Swipe through call cards</li>
              <li>Tap to expand details</li>
              <li>Quick action buttons for calling back</li>
            </ul>
          `,
          relatedArticles: ['call-recording', 'making-calls']
        },
        {
          id: 'call-recording',
          title: 'Call Recording',
          description: 'Enable, manage, and play back call recordings',
          tags: ['recording', 'compliance'],
          content: `
            <h2>Call Recording Guide</h2>
            <p>Record calls for training, quality assurance, or compliance.</p>

            <h3>Important Legal Notice</h3>
            <p><strong>⚠️ Recording laws vary by location.</strong> You may need:</p>
            <ul>
              <li>Consent from all parties (two-party consent states)</li>
              <li>Disclosure announcement at start of call</li>
              <li>Written policies for employee recording</li>
            </ul>
            <p>Consult with legal counsel before enabling call recording.</p>

            <h3>Enabling Call Recording</h3>
            <ol>
              <li>Navigate to <strong>Settings > Recording Rules</strong></li>
              <li>Choose your recording mode:
                <ul>
                  <li><strong>Always Record:</strong> All calls recorded automatically</li>
                  <li><strong>On Demand:</strong> Press *1 during call to start</li>
                  <li><strong>Selective:</strong> Set rules by extension/direction</li>
                  <li><strong>Never:</strong> Recording disabled</li>
                </ul>
              </li>
              <li>Configure recording disclosure (recommended)</li>
              <li>Click <strong>Save</strong></li>
            </ol>

            <h3>Recording Rules</h3>
            <p>You can create custom rules like:</p>
            <ul>
              <li>Record all inbound calls, but not outbound</li>
              <li>Record only calls to customer service extensions</li>
              <li>Record calls to specific phone numbers only</li>
              <li>Exclude internal extension-to-extension calls</li>
            </ul>

            <h3>Playing Recordings</h3>
            <ol>
              <li>Go to <strong>Call History</strong></li>
              <li>Find a call with recording available (shows play icon)</li>
              <li>Click <strong>Play</strong> to listen in browser</li>
              <li>Or click <strong>Download</strong> to save MP3</li>
            </ol>

            <p><strong>Note:</strong> Recordings usually available within 60 seconds of call ending.</p>

            <h3>Recording Storage</h3>
            <ul>
              <li><strong>Retention:</strong> Recordings kept for 90 days by default</li>
              <li><strong>Storage Limits:</strong> Based on your plan</li>
              <li><strong>Download Before Deletion:</strong> Save important recordings locally</li>
            </ul>

            <h3>Best Practices</h3>
            <ul>
              <li>✅ Enable disclosure announcement</li>
              <li>✅ Have written recording policy</li>
              <li>✅ Limit access to sensitive recordings</li>
              <li>✅ Download and archive critical recordings</li>
              <li>✅ Regularly review storage usage</li>
              <li>❌ Don't record payment card information (PCI violation)</li>
            </ul>

            <h3>Troubleshooting</h3>
            <p><strong>Recording not appearing?</strong></p>
            <ul>
              <li>Wait 60 seconds after call ends</li>
              <li>Check recording rules are enabled for that extension/direction</li>
              <li>Verify sufficient storage space</li>
              <li>Refresh call history page</li>
            </ul>

            <p><strong>Can't play recording?</strong></p>
            <ul>
              <li>Try downloading instead of streaming</li>
              <li>Check browser audio permissions</li>
              <li>Disable browser extensions blocking audio</li>
              <li>Try incognito/private mode</li>
            </ul>
          `,
          relatedArticles: ['call-history', 'recording-rules']
        },
        {
          id: 'call-forwarding',
          title: 'Call Forwarding',
          description: 'Forward calls to other numbers or extensions',
          tags: ['forwarding', 'routing'],
          content: `
            <h2>Call Forwarding Setup</h2>
            <p>Never miss a call - forward them to any number.</p>

            <h3>Types of Call Forwarding</h3>

            <h4>1. Always Forward</h4>
            <p>All calls immediately forwarded to another number.</p>
            <ul>
              <li>Good for: Working remotely, traveling</li>
              <li>Calls won't ring your desk phone</li>
            </ul>

            <h4>2. Forward on No Answer</h4>
            <p>Forwards only if you don't answer within X rings.</p>
            <ul>
              <li>Good for: Backup coverage</li>
              <li>Usually set to 4-5 rings (20-25 seconds)</li>
            </ul>

            <h4>3. Forward on Busy</h4>
            <p>Forwards when you're already on another call.</p>
            <ul>
              <li>Good for: Ensuring no missed calls</li>
              <li>Can forward to voicemail or another person</li>
            </ul>

            <h4>4. Forward on Unavailable</h4>
            <p>Forwards if your phone is offline/unreachable.</p>
            <ul>
              <li>Good for: Network outages</li>
              <li>Automatic failover</li>
            </ul>

            <h3>Setting Up Forwarding</h3>
            <ol>
              <li>Go to <strong>Extensions</strong> in sidebar</li>
              <li>Click on your extension number</li>
              <li>Scroll to <strong>Call Forwarding</strong> section</li>
              <li>Enable the type(s) you want</li>
              <li>Enter destination number (with area code)</li>
              <li>Set number of rings (for "no answer" type)</li>
              <li>Click <strong>Save</strong></li>
            </ol>

            <h3>Quick Codes (Phone Method)</h3>
            <p>Activate forwarding directly from your phone:</p>
            <ul>
              <li><strong>*72</strong> + destination number = Enable always forward</li>
              <li><strong>*73</strong> = Disable always forward</li>
              <li><strong>*90</strong> + number = Forward on busy</li>
              <li><strong>*91</strong> = Cancel forward on busy</li>
            </ul>

            <h3>Advanced: Conditional Forwarding</h3>
            <p>Create rules based on:</p>
            <ul>
              <li><strong>Time of day:</strong> Forward after hours</li>
              <li><strong>Caller ID:</strong> VIP callers to mobile, others to voicemail</li>
              <li><strong>Day of week:</strong> Different numbers for weekends</li>
            </ul>

            <h3>Things to Know</h3>
            <ul>
              <li>✅ Can forward to external numbers or internal extensions</li>
              <li>✅ Forwarded calls still appear in your call history</li>
              <li>✅ Caller ID shows original caller, not your number</li>
              <li>⚠️ Long distance charges apply if forwarding to long distance number</li>
              <li>⚠️ Can't forward to premium numbers (900, etc.)</li>
            </ul>

            <h3>Testing Your Forwarding</h3>
            <ol>
              <li>Set up forwarding to your mobile</li>
              <li>Call your extension from another phone</li>
              <li>Verify mobile rings</li>
              <li>Answer and confirm caller ID shows correctly</li>
            </ol>

            <h3>Troubleshooting</h3>
            <p><strong>Forwarding not working?</strong></p>
            <ul>
              <li>Verify destination number format (include area code)</li>
              <li>Check that you clicked "Save"</li>
              <li>Ensure feature is enabled on your account</li>
              <li>Try using phone codes instead of portal</li>
              <li>Contact support if issues persist</li>
            </ul>
          `,
          relatedArticles: ['extensions-setup', 'voicemail-setup', 'time-conditions']
        }
      ]
    },
    {
      id: 'billing',
      name: 'Billing & Payments',
      description: 'Manage your account billing and payments',
      icon: 'article',
      articles: [
        {
          id: 'understanding-invoice',
          title: 'Understanding Your Invoice',
          description: 'Learn how to read and understand your monthly bill',
          popular: true,
          tags: ['billing', 'invoice'],
          content: `
            <h2>Invoice Breakdown</h2>
            <p>Your invoice is broken down into several sections.</p>

            <h3>1. Account Summary</h3>
            <ul>
              <li><strong>Invoice Date:</strong> When the bill was generated</li>
              <li><strong>Due Date:</strong> When payment is due</li>
              <li><strong>Invoice Number:</strong> For reference when contacting support</li>
              <li><strong>Total Amount Due:</strong> What you owe this period</li>
            </ul>

            <h3>2. Recurring Charges</h3>
            <p>Monthly fees that repeat every billing cycle:</p>
            <ul>
              <li><strong>Base Service:</strong> Your main package/plan</li>
              <li><strong>Extensions:</strong> Per-extension fees</li>
              <li><strong>Phone Numbers:</strong> Monthly phone number fees</li>
              <li><strong>Add-on Features:</strong> Call recording, fax, etc.</li>
            </ul>

            <h3>3. Usage Charges</h3>
            <p>Variable costs based on your usage:</p>
            <ul>
              <li><strong>Long Distance:</strong> Calls outside local calling area</li>
              <li><strong>International:</strong> Calls to other countries (per destination)</li>
              <li><strong>SMS/Text Messages:</strong> If applicable</li>
              <li><strong>Overage:</strong> If you exceeded included minutes</li>
            </ul>

            <h3>4. Credits & Adjustments</h3>
            <p>Any discounts or credits applied:</p>
            <ul>
              <li>Promotional credits</li>
              <li>Service issue adjustments</li>
              <li>Refunds or account credits</li>
            </ul>

            <h3>5. Taxes & Regulatory Fees</h3>
            <p>Government-mandated charges:</p>
            <ul>
              <li>Federal taxes</li>
              <li>State/provincial taxes</li>
              <li>E911 fees</li>
              <li>Regulatory recovery fees</li>
            </ul>

            <h3>Common Line Items Explained</h3>

            <p><strong>Phone Number</strong><br/>
            Monthly fee for each phone number assigned to your account.</p>

            <p><strong>Extension Seat</strong><br/>
            Per-user fee for each extension/user on your system.</p>

            <p><strong>Outbound Long Distance</strong><br/>
            Long distance calling charges (rates vary by destination).</p>

            <p><strong>Call Recording Storage</strong><br/>
            If you have call recording enabled, storage fees may apply based on usage.</p>

            <h3>Viewing Past Invoices</h3>
            <ol>
              <li>Go to <strong>Billing</strong> in sidebar</li>
              <li>Click <strong>Invoice History</strong></li>
              <li>Click any invoice to view details</li>
              <li>Download PDF or print for records</li>
            </ol>

            <h3>Questions About Charges?</h3>
            <p>If something doesn't look right:</p>
            <ol>
              <li>Check usage details in Call History</li>
              <li>Compare to previous invoices</li>
              <li>Contact billing support with invoice number</li>
              <li>Adjustments typically processed within 2-3 business days</li>
            </ol>
          `,
          relatedArticles: ['payment-methods', 'auto-recharge']
        },
        {
          id: 'payment-methods',
          title: 'Managing Payment Methods',
          description: 'Add, update, or remove payment methods',
          tags: ['billing', 'payment', 'credit card'],
          content: `
            <h2>Payment Methods</h2>
            <p>Manage your credit cards and payment options securely.</p>

            <h3>Adding a Payment Method</h3>
            <ol>
              <li>Go to <strong>Billing > Payment Methods</strong></li>
              <li>Click <strong>Add Payment Method</strong></li>
              <li>Enter card details:
                <ul>
                  <li>Card number (16 digits)</li>
                  <li>Expiration date (MM/YY)</li>
                  <li>CVV (3-4 digit security code)</li>
                  <li>Billing ZIP/postal code</li>
                </ul>
              </li>
              <li>Click <strong>Save Card</strong></li>
            </ol>

            <h3>Supported Payment Methods</h3>
            <ul>
              <li>✅ Visa</li>
              <li>✅ Mastercard</li>
              <li>✅ American Express</li>
              <li>✅ Discover</li>
              <li>❌ Prepaid cards (may be declined)</li>
            </ul>

            <h3>Security & PCI Compliance</h3>
            <p>Your payment information is protected:</p>
            <ul>
              <li>🔒 256-bit SSL encryption</li>
              <li>🔒 PCI DSS Level 1 compliant</li>
              <li>🔒 Card numbers are tokenized</li>
              <li>🔒 We never store full card numbers</li>
            </ul>

            <h3>Setting Default Payment Method</h3>
            <ol>
              <li>Go to <strong>Payment Methods</strong></li>
              <li>Find the card you want as default</li>
              <li>Click <strong>Set as Default</strong></li>
            </ol>
            <p>Your default card will be charged automatically for invoices.</p>

            <h3>Updating Card Information</h3>
            <p>When you get a new card or expiration date:</p>
            <ol>
              <li>Go to <strong>Payment Methods</strong></li>
              <li>Click <strong>Edit</strong> on the card</li>
              <li>Update expiration date or CVV</li>
              <li>Click <strong>Save</strong></li>
            </ol>

            <p><strong>Note:</strong> You cannot edit card number. Add a new card and delete old one instead.</p>

            <h3>Removing a Card</h3>
            <ol>
              <li>Go to <strong>Payment Methods</strong></li>
              <li>Click <strong>Delete</strong> on the card</li>
              <li>Confirm removal</li>
            </ol>

            <p>⚠️ <strong>Important:</strong> You must have at least one payment method on file.</p>

            <h3>Failed Payments</h3>
            <p>If your card is declined:</p>
            <ol>
              <li>Check card hasn't expired</li>
              <li>Verify billing address matches card</li>
              <li>Ensure sufficient funds/credit available</li>
              <li>Contact your bank if issue persists</li>
              <li>Update payment method in portal</li>
            </ol>

            <p>We'll retry failed payments after 3 days automatically.</p>

            <h3>Payment Receipts</h3>
            <p>After successful payment:</p>
            <ul>
              <li>Receipt emailed to account email</li>
              <li>Available in Billing > Payment History</li>
              <li>Downloadable as PDF</li>
            </ul>
          `,
          relatedArticles: ['understanding-invoice', 'auto-recharge']
        },
        {
          id: 'auto-recharge',
          title: 'Auto-Recharge Setup',
          description: 'Automatically add funds when balance runs low',
          tags: ['billing', 'auto-recharge', 'prepaid'],
          content: `
            <h2>Auto-Recharge Guide</h2>
            <p>Never run out of service - set up automatic recharges.</p>

            <h3>What is Auto-Recharge?</h3>
            <p>Auto-recharge automatically adds funds to your account when balance falls below a threshold you set.</p>

            <p><strong>Benefits:</strong></p>
            <ul>
              <li>✅ No service interruptions</li>
              <li>✅ No manual monitoring needed</li>
              <li>✅ Convenient and automatic</li>
              <li>✅ Control amount and threshold</li>
            </ul>

            <h3>Setting Up Auto-Recharge</h3>
            <ol>
              <li>Go to <strong>Billing > Auto-Recharge</strong></li>
              <li>Click <strong>Enable Auto-Recharge</strong></li>
              <li>Set your preferences:
                <ul>
                  <li><strong>Trigger threshold:</strong> When to recharge (e.g., $10 remaining)</li>
                  <li><strong>Recharge amount:</strong> How much to add (e.g., $50)</li>
                  <li><strong>Maximum per month:</strong> Optional spending limit</li>
                </ul>
              </li>
              <li>Select payment method</li>
              <li>Click <strong>Save Settings</strong></li>
            </ol>

            <h3>How It Works</h3>
            <p>Example scenario:</p>
            <ol>
              <li>Your balance is $100</li>
              <li>You've set threshold at $10</li>
              <li>You've set recharge amount to $50</li>
              <li>When balance hits $10, system automatically charges your card $50</li>
              <li>New balance becomes $60</li>
              <li>You receive email notification</li>
            </ol>

            <h3>Recommended Settings</h3>

            <p><strong>Light Users (few calls/month):</strong></p>
            <ul>
              <li>Threshold: $5</li>
              <li>Amount: $25</li>
              <li>Max/month: $50</li>
            </ul>

            <p><strong>Medium Users (regular business use):</strong></p>
            <ul>
              <li>Threshold: $10</li>
              <li>Amount: $50</li>
              <li>Max/month: $200</li>
            </ul>

            <p><strong>Heavy Users (high volume):</strong></p>
            <ul>
              <li>Threshold: $25</li>
              <li>Amount: $100</li>
              <li>Max/month: $500</li>
            </ul>

            <h3>Notifications</h3>
            <p>You'll receive email when:</p>
            <ul>
              <li>Auto-recharge is triggered</li>
              <li>Recharge completes successfully</li>
              <li>Payment fails (with retry notification)</li>
              <li>Monthly maximum is reached</li>
            </ul>

            <h3>Safety Features</h3>
            <ul>
              <li><strong>Monthly Cap:</strong> Prevents excessive charges if usage spikes</li>
              <li><strong>Email Alerts:</strong> Keep track of all recharges</li>
              <li><strong>Payment Retry:</strong> If card fails, we'll notify you</li>
              <li><strong>Disable Anytime:</strong> Turn off auto-recharge whenever needed</li>
            </ul>

            <h3>Disabling Auto-Recharge</h3>
            <p>To turn off auto-recharge:</p>
            <ol>
              <li>Go to <strong>Billing > Auto-Recharge</strong></li>
              <li>Toggle <strong>Auto Top-up</strong> to off</li>
              <li>Click <strong>Save Settings</strong> to confirm</li>
            </ol>

            <p>Your settings will be saved if you want to enable it again later.</p>

            <p>⚠️ <strong>Warning:</strong> If balance hits $0, service may be interrupted.</p>

            <h3>Troubleshooting</h3>

            <p><strong>Auto-recharge didn't trigger?</strong></p>
            <ul>
              <li>Check that it's enabled (not paused)</li>
              <li>Verify balance is below threshold</li>
              <li>Confirm payment method is valid</li>
              <li>Check if monthly maximum was reached</li>
            </ul>

            <p><strong>Payment failed?</strong></p>
            <ul>
              <li>Update payment method</li>
              <li>Manually add funds to restore service</li>
              <li>System will retry in 24 hours</li>
            </ul>
          `,
          relatedArticles: ['payment-methods', 'understanding-invoice']
        }
      ]
    },
    {
      id: 'emergency',
      name: 'Emergency Services',
      description: '911/E911 setup and emergency address management',
      icon: 'article',
      articles: [
        {
          id: 'e911-setup',
          title: 'E911 Setup & Requirements',
          description: 'Configure your emergency address for 911 calls',
          popular: true,
          tags: ['911', 'E911', 'emergency', 'safety'],
          content: `
            <h2>E911 Emergency Service Setup</h2>
            <p><strong>⚠️ CRITICAL:</strong> Proper E911 setup is required for emergency services to locate you.</p>

            <h3>What is E911?</h3>
            <p>Enhanced 911 (E911) provides your physical location to emergency responders when you dial 911. Unlike traditional phones, VoIP requires you to manually register your location.</p>

            <h3>Why It's Important</h3>
            <ul>
              <li>🚨 Paramedics need to know WHERE to send help</li>
              <li>🚨 VoIP calls don't automatically provide location</li>
              <li>🚨 FCC requires all VoIP providers to offer E911</li>
              <li>🚨 Incorrect address = delayed emergency response</li>
            </ul>

            <h3>Setting Up Your E911 Address</h3>

            <h4>Step 1: Navigate to E911 Settings</h4>
            <ol>
              <li>Click <strong>Settings</strong> in sidebar</li>
              <li>Select <strong>Emergency 911</strong></li>
              <li>Click <strong>Add Emergency Address</strong></li>
            </ol>

            <h4>Step 2: Enter Your Physical Location</h4>
            <p><strong>⚠️ Use your PHYSICAL location, NOT mailing address!</strong></p>

            <p>Required information:</p>
            <ul>
              <li><strong>Street Number:</strong> Building number (e.g., 123)</li>
              <li><strong>Street Name:</strong> Full street name (e.g., Main Street)</li>
              <li><strong>Suite/Unit:</strong> Apartment, suite, floor number</li>
              <li><strong>City:</strong> Must match official city name</li>
              <li><strong>State/Province:</strong> 2-letter code (e.g., ON, CA, NY)</li>
              <li><strong>ZIP/Postal Code:</strong> Must be valid</li>
              <li><strong>Country:</strong> USA or Canada</li>
            </ul>

            <h4>Step 3: Address Validation</h4>
            <p>After entering address:</p>
            <ol>
              <li>System validates against MSAG (Master Street Address Guide)</li>
              <li>You may see suggested corrections</li>
              <li>Accept suggested address for best results</li>
              <li>Click <strong>Save & Validate</strong></li>
            </ol>

            <p>✅ <strong>Valid:</strong> Green checkmark = address confirmed<br/>
            ❌ <strong>Invalid:</strong> Red X = address not found in database</p>

            <h3>Special Situations</h3>

            <h4>Multi-Floor Buildings</h4>
            <p>If you're in a large building:</p>
            <ul>
              <li>Include suite/floor number</li>
              <li>Add descriptive location (e.g., "3rd Floor, East Wing")</li>
              <li>Consider setting different addresses per floor</li>
            </ul>

            <h4>Rural Addresses</h4>
            <p>For rural/farm addresses:</p>
            <ul>
              <li>Use official 911 address if available</li>
              <li>Include rural route number</li>
              <li>Add landmark descriptions</li>
              <li>Contact local 911 center to verify</li>
            </ul>

            <h4>Remote Workers</h4>
            <p>If working from home:</p>
            <ul>
              <li>Set home address as E911 location</li>
              <li>Update when you move or travel</li>
              <li>Don't use office address if working remotely</li>
            </ul>

            <h3>Assigning Addresses to Extensions</h3>
            <ol>
              <li>Go to <strong>Extensions</strong></li>
              <li>Click on an extension</li>
              <li>Under <strong>E911 Address</strong>, select from dropdown</li>
              <li>Each extension can have different address</li>
              <li>Click <strong>Save</strong></li>
            </ol>

            <h3>Testing (DO NOT TEST BY CALLING 911!)</h3>
            <p><strong>⚠️ NEVER dial 911 to test!</strong></p>

            <p>Instead:</p>
            <ul>
              <li>Verify address shows correctly in portal</li>
              <li>Check "Last Validated" date is recent</li>
              <li>Look for green ✅ validation checkmark</li>
              <li>Some areas offer non-emergency test lines - contact local 911 center</li>
            </ul>

            <h3>Important Reminders</h3>
            <ul>
              <li>🔔 <strong>Update Immediately:</strong> When you move offices</li>
              <li>🔔 <strong>Verify Regularly:</strong> Check addresses quarterly</li>
              <li>🔔 <strong>Inform Staff:</strong> Tell employees how to dial 911</li>
              <li>🔔 <strong>Alternative Contact:</strong> Keep cell phone nearby as backup</li>
            </ul>

            <h3>Limitations of VoIP 911</h3>
            <p>Be aware:</p>
            <ul>
              <li>❌ Doesn't work during power/internet outages</li>
              <li>❌ Location not updated if you move device without updating</li>
              <li>❌ May have slight delay vs. traditional phones</li>
              <li>❌ Calling from softphone? Ensure address matches location</li>
            </ul>

            <h3>In Case of Emergency</h3>
            <ol>
              <li><strong>Dial 911</strong> from your VoIP phone</li>
              <li><strong>State your address</strong> immediately (don't assume they have it)</li>
              <li><strong>Describe the emergency</strong> clearly</li>
              <li><strong>Don't hang up</strong> until told to do so</li>
              <li><strong>Stay on the line</strong> for further instructions</li>
            </ol>

            <p><strong>Backup Plan:</strong> Keep a cell phone charged for emergencies.</p>

            <h3>Troubleshooting</h3>

            <p><strong>Address won't validate?</strong></p>
            <ul>
              <li>Try suggested address corrections</li>
              <li>Verify spelling and abbreviations</li>
              <li>Check with local 911 center for official address format</li>
              <li>Contact support if issues persist</li>
            </ul>

            <p><strong>Need to change address?</strong></p>
            <ul>
              <li>Edit existing address anytime</li>
              <li>Changes take effect immediately</li>
              <li>Must re-validate after changes</li>
            </ul>

            <h3>Compliance & Legal</h3>
            <p>FCC regulations require:</p>
            <ul>
              <li>All VoIP users must have E911 capability</li>
              <li>Addresses must be accurate and current</li>
              <li>Users must be notified of E911 limitations</li>
              <li>Failure to maintain accurate info may violate regulations</li>
            </ul>

            <p><strong>Your safety depends on accurate E911 information. Update it today!</strong></p>
          `,
          relatedArticles: ['extensions-setup']
        }
      ]
    }
  ]
};
